import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { playbackUrl, diskNameFromUrl, useTranscodeStatus } from '../../tools/videoSource';
import { withExtension } from '../../tools/downloadName';

// ── In-app media viewer (Digital Marketing) ───────────────────────────────────
// Everything plays/renders INSIDE the app — no window.open, no browser tabs.
// Handles image / video / audio / PDF inline; anything else gets a download row.
// media: { url, name, kind } — kind resolved by resolveMediaKind below.

export const resolveMediaKind = (nameOrMime = '') => {
  const s = String(nameOrMime).toLowerCase();
  if (/(^image\/)|\.(jpe?g|png|gif|webp|bmp|svg)$/.test(s)) return 'image';
  // Deliberately wide: the app accepts whatever a phone or a camera produces,
  // and the odd containers are exactly the ones that need the transcoded copy.
  if (/(^video\/)|\.(mp4|webm|ogv|mov|mkv|avi|m4v|wmv|flv|3gp|3g2|mpe?g|ts|m2ts|mts|f4v|asf|divx|vob)$/.test(s)) return 'video';
  if (/(^audio\/)|\.(mp3|wav|ogg|m4a|aac|webm;codecs)$/.test(s)) return 'audio';
  if (/(^application\/pdf)|\.pdf$/.test(s))                 return 'pdf';
  return 'other';
};

// What KIND of thing is this stored file? Takes the whole entry, because the
// answer is only reliable when the three things it carries are tried in the
// right order:
//
//   1. mimetype  — snapshotted by the upload route from what the browser sent.
//                  Authoritative, and the ONLY one that survives both a rename
//                  and an upload whose filename had no extension at all.
//   2. diskName  — the server-generated name. It keeps the original extension
//                  when there was one, and is never edited afterwards.
//   3. name      — last, because it is the DISPLAY name: the file edit dialog
//                  lets anyone rename "clip.mp4" to "B-roll 1", and a phone or
//                  a share sheet can hand over a file with no extension in the
//                  first place.
//
// Reading `name` FIRST is what made every attachment in Digital Marketing show
// as a grey "file" tile: `name || diskName` short-circuits on a perfectly
// truthy name that simply has no extension, so the extension never got looked
// at and the mimetype never got looked at either.
export const resolveEntryKind = (entry) => {
  if (!entry) return 'other';
  const fromMime = entry.mimetype ? resolveMediaKind(entry.mimetype) : 'other';
  if (fromMime !== 'other') return fromMime;
  const fromDisk = entry.diskName ? resolveMediaKind(entry.diskName) : 'other';
  if (fromDisk !== 'other') return fromDisk;
  return resolveMediaKind(entry.name || '');
};

// Native browser download WITH progress. We route a /uploads/<diskName> URL
// through the API's /download/<diskName> endpoint, which sets
// `Content-Disposition: attachment` — so the browser saves the file with its OWN
// download manager (progress bar, resumable) instead of us blob-fetching the
// whole thing into memory first (no progress, delayed, opens a tab).
// The cross-origin `download` attribute is ignored, but Content-Disposition
// forces the save regardless of origin.
//
// `name` is usually a display TITLE with no extension (the File Manager stores
// names without one, and Digital Marketing lets people type their own), so it
// is completed with the extension of the file itself — read off the
// /uploads/<diskName> URL — before it is sent. The server does the same check
// (api/utils/downloadName.js); doing it here too means the name is right even
// before the server is updated. Without it the file reached the person's disk
// with no format.
export const downloadFile = (url, name) => {
  const savedName = name ? withExtension(name, url) : '';
  let href = url;
  const marker = '/uploads/';
  const i = url.indexOf(marker);
  if (i !== -1) {
    href = url.slice(0, i) + '/download/' + url.slice(i + marker.length);
    if (savedName) href += (href.includes('?') ? '&' : '?') + 'name=' + encodeURIComponent(savedName);
  }
  const a = document.createElement('a');
  a.href = href;
  if (savedName) a.download = savedName;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

// sx — optional passthrough to the root Dialog (e.g. a zIndex bump when this
// viewer can be opened from inside another Dialog/Drawer — see tutorialForm.js
// / tutorialDetail.js, which nest it inside sectionTutorials.js's Dialog).
const MediaViewer = ({ open, onClose, media, sx }) => {
  const { t }  = useTranslation();
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { url, name = '' } = media || {};
  const kind = media ? (media.kind || resolveMediaKind(name)) : 'other';

  // Hooks must run on every render, so this sits above the `!media` bail-out.
  // Only asked for while a video is actually on screen.
  const transcodeStatus = useTranscodeStatus(
    kind === 'video' ? diskNameFromUrl(url) : null,
    Boolean(open && media && kind === 'video'),
  );

  if (!media) return null;

  const handleDownload = () => downloadFile(url, name);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={sx}
      PaperProps={{ sx: {
        bgcolor: isDark ? '#0d0d0d' : theme.palette.background.paper,
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : theme.palette.divider}`,
        borderRadius: '14px', backgroundImage: 'none', overflow: 'hidden',
      }}}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25,
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : theme.palette.divider}` }}>
        <Typography noWrap sx={{ flexGrow: 1, fontSize: '0.82rem', fontWeight: 600,
          color: isDark ? 'rgba(255,255,255,0.85)' : 'text.primary' }}>
          {name || t('common.preview')}
        </Typography>
        <IconButton size="small" onClick={handleDownload}
          sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>
          <DownloadIcon sx={{ fontSize: 17 }} />
        </IconButton>
        <IconButton size="small" onClick={onClose}
          sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>
          <CloseIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: isDark ? '#000' : 'rgba(0,0,0,0.03)', minHeight: 220 }}>
        {kind === 'image' && (
          <Box component="img" src={url} alt={name}
            sx={{ maxWidth: '100%', maxHeight: '72vh', objectFit: 'contain', display: 'block' }} />
        )}
        {/* playbackUrl() points at the transcoded H.264/AAC copy when the
            upload's own container/codec isn't one the browser can decode, and
            at the original otherwise — see tools/videoSource.js. */}
        {kind === 'video' && transcodeStatus !== 'pending' && (
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box component="video" src={playbackUrl(url)} controls autoPlay playsInline
              sx={{ maxWidth: '100%', maxHeight: '72vh', display: 'block', outline: 'none' }} />
            {transcodeStatus === 'failed' && (
              <Typography sx={{ py: 1.25, px: 2, fontSize: '0.72rem', textAlign: 'center',
                color: isDark ? 'rgba(255,255,255,0.45)' : 'text.secondary' }}>
                {t('common.videoConvertFailed')}
              </Typography>
            )}
          </Box>
        )}
        {/* A conversion still running would otherwise show as a black frame
            with working controls and no picture — say so instead. */}
        {kind === 'video' && transcodeStatus === 'pending' && (
          <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={26} thickness={4}
              sx={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)' }} />
            <Typography sx={{ fontSize: '0.8rem', color: isDark ? 'rgba(255,255,255,0.45)' : 'text.secondary' }}>
              {t('common.videoPreparing')}
            </Typography>
          </Box>
        )}
        {kind === 'audio' && (
          <Box sx={{ py: 5, px: 3, width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Box component="audio" src={url} controls autoPlay sx={{ width: '100%', maxWidth: 420 }} />
          </Box>
        )}
        {kind === 'pdf' && (
          <Box component="iframe" src={url} title={name}
            sx={{ width: '100%', height: '72vh', border: 'none', bgcolor: '#fff' }} />
        )}
        {kind === 'other' && (
          <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <InsertDriveFileIcon sx={{ fontSize: 44, color: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.25)' }} />
            <Typography sx={{ fontSize: '0.8rem', color: isDark ? 'rgba(255,255,255,0.45)' : 'text.secondary' }}>
              {t('common.noInlinePreview')}
            </Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default MediaViewer;
